import { useMutation, useQueryClient } from '@tanstack/react-query';
import { stringifyUrl } from 'query-string';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { fetcher } from '@dashboard/services/request';
import {
  adminMerchantKey,
  adminPaymentsKey,
  adminStuckFreeChargeKey,
  adminBulkBookingImportsKey,
  adminTemplateKey,
  adminTemplateCategoryKey,
  adminBrandLogKey,
  adminAiUsageKey,
} from '@dashboard/lib/queryKeys';

/**
 * Hook for updating merchant data (general seller update)
 * Used for updating custom pricing, feature flags, etc.
 * @param {Object} options - Additional react-query mutation options
 */
export function useUpdateMerchant(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  return useMutation({
    mutationFn: ({ sellerId, data }) =>
      fetcher(`/admin/sellers/${sellerId}`, token, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminMerchantKey(variables.sellerId) });
      queryClient.invalidateQueries({ queryKey: ['admin', 'merchants'] });
      onSuccess?.(data, variables, context);
    },
    ...rest,
  });
}

/**
 * Hook for updating merchant industry
 * @param {Object} options - Additional react-query mutation options
 */
export function useUpdateMerchantIndustry(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sellerId, industry }) =>
      fetcher(`/admin/sellers/${sellerId}`, token, {
        method: 'POST',
        body: JSON.stringify({ industry }),
      }),
    ...options,
    // Invalidate before the caller's handler and after the spread, so a
    // component passing its own onSuccess cannot silently replace the refresh.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminMerchantKey(variables.sellerId) });
      options.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Hook for updating a team member's email settings.
 * Turning marketingConsent off also unsubscribes them in the email marketing software.
 * @param {Object} options - Additional react-query mutation options
 */
export function useUpdateTeamMemberEmailSettings(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const { onError, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ sellerId, userId, settings }) =>
      fetcher(`/admin/sellers/${sellerId}/users/${userId}/email-settings`, token, {
        method: 'POST',
        body: JSON.stringify(settings),
      }),
    onMutate: async ({ sellerId, userId, settings }) => {
      const queryKey = adminMerchantKey(sellerId);

      await queryClient.cancelQueries({ queryKey });

      const previousMerchant = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, merchant => {
        if (!merchant) return merchant;

        return {
          ...merchant,
          users: merchant.users?.map(teamMember =>
            teamMember.user?._id === userId
              ? { ...teamMember, user: { ...teamMember.user, ...settings } }
              : teamMember,
          ),
        };
      });

      return { previousMerchant };
    },
    onError: (error, variables, context) => {
      if (context?.previousMerchant) {
        queryClient.setQueryData(adminMerchantKey(variables.sellerId), context.previousMerchant);
      }

      onError?.(error, variables, context);
    },
    onSettled: (_data, _error, { sellerId }) => {
      queryClient.invalidateQueries({ queryKey: adminMerchantKey(sellerId) });
    },
    ...restOptions,
  });
}

/**
 * Hook for disabling a seller
 * @param {Object} options - Additional react-query mutation options
 */
export function useDisableSeller(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sellerId, reason, notify }) =>
      fetcher(`/admin/sellers/${sellerId}/disable`, token, {
        method: 'POST',
        body: JSON.stringify({ reason, notify }),
      }),
    ...options,
    // Invalidate before the caller's handler and after the spread, so a
    // component passing its own onSuccess cannot silently replace the refresh.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminMerchantKey(variables.sellerId) });
      queryClient.invalidateQueries({ queryKey: ['admin', 'merchants'] });
      options.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Hook for enabling a seller
 * @param {Object} options - Additional react-query mutation options
 */
export function useEnableSeller(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sellerId }) =>
      fetcher(`/admin/sellers/${sellerId}/enable`, token, {
        method: 'POST',
      }),
    ...options,
    // Invalidate before the caller's handler and after the spread, so a
    // component passing its own onSuccess cannot silently replace the refresh.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminMerchantKey(variables.sellerId) });
      queryClient.invalidateQueries({ queryKey: ['admin', 'merchants'] });
      options.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Hook for canceling merchant subscription
 * @param {Object} options - Additional react-query mutation options
 */
export function useCancelSubscription(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sellerId }) =>
      fetcher(`/admin/sellers/${sellerId}/cancel-subscription`, token, {
        method: 'POST',
      }),
    ...options,
    // Invalidate before the caller's handler and after the spread, so a
    // component passing its own onSuccess cannot silently replace the refresh.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminMerchantKey(variables.sellerId) });
      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useFinalizeStuckFreeCharge(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chargeId }) =>
      fetcher(`/admin/charges/${chargeId}/finalize-free`, token, {
        method: 'POST',
      }),
    ...options,
    // Invalidate before the caller's handler and after the spread, so a
    // component passing its own onSuccess cannot silently replace the refresh.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminStuckFreeChargeKey(variables.chargeId) });
      queryClient.invalidateQueries({ queryKey: adminPaymentsKey() });
      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useStartBulkBookingImport(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ csv }) =>
      fetcher('/admin/bulk-booking-imports', token, {
        method: 'POST',
        body: JSON.stringify({ csv }),
      }),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminBulkBookingImportsKey() });
      options.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Hook for expiring merchant trial
 * @param {Object} options - Additional react-query mutation options
 */
export function useExpireTrial(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sellerId }) =>
      fetcher(`/admin/sellers/${sellerId}/expire-trial`, token, {
        method: 'POST',
      }),
    ...options,
    // Invalidate before the caller's handler and after the spread, so a
    // component passing its own onSuccess cannot silently replace the refresh.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminMerchantKey(variables.sellerId) });
      options.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Hook for extending merchant trial
 * @param {Object} options - Additional react-query mutation options
 */
export function useExtendTrial(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sellerId, days }) =>
      fetcher(`/admin/sellers/${sellerId}/extend-trial`, token, {
        method: 'POST',
        body: JSON.stringify({ days }),
      }),
    ...options,
    // Invalidate before the caller's handler and after the spread, so a
    // component passing its own onSuccess cannot silently replace the refresh.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminMerchantKey(variables.sellerId) });
      options.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Hook for granting a merchant a paid plan with no Stripe subscription
 * @param {Object} options - Additional react-query mutation options
 */
export function useCompSeller(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sellerId, planHandle, billingPeriod, bracket, reason }) =>
      fetcher(`/admin/sellers/${sellerId}/comp`, token, {
        method: 'POST',
        body: JSON.stringify({ planHandle, billingPeriod, bracket, reason }),
      }),
    ...options,
    // Invalidate before the caller's handler and after the spread, so a
    // component passing its own onSuccess cannot silently replace the refresh.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminMerchantKey(variables.sellerId) });
      options.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Hook for ending a comp and returning the merchant to Free
 * @param {Object} options - Additional react-query mutation options
 */
export function useRemoveComp(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sellerId }) =>
      fetcher(`/admin/sellers/${sellerId}/remove-comp`, token, {
        method: 'POST',
      }),
    ...options,
    // Invalidate before the caller's handler and after the spread, so a
    // component passing its own onSuccess cannot silently replace the refresh.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminMerchantKey(variables.sellerId) });
      options.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Hook for deleting a user
 * @param {Object} options - Additional react-query mutation options
 */
export function useDeleteUser(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId }) =>
      fetcher(`/admin/users/${userId}/delete`, token, {
        method: 'POST',
      }),
    ...options,
    // Invalidate before the caller's handler and after the spread, so a
    // component passing its own onSuccess cannot silently replace the refresh.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'merchants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      options.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Hook for disabling all stores owned by a user
 */
export function useDisableUserStores(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, reason, notify }) =>
      fetcher(`/admin/users/${userId}/disable-stores`, token, {
        method: 'POST',
        body: JSON.stringify({ reason, notify }),
      }),
    ...options,
    // Invalidate before the caller's handler and after the spread, so a
    // component passing its own onSuccess cannot silently replace the refresh.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'merchants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      options.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Hook for starting statistics aggregation for a merchant
 * @param {Object} options - Additional react-query mutation options
 */
export function useStartStatisticsAggregate(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sellerId, range, metric }) =>
      fetcher(
        stringifyUrl({
          url: `/admin/sellers/${sellerId}/aggregate-historical-statistics`,
          query: {
            range,
            ...(metric && { metric }),
          },
        }),
        token,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'statistic-logs'] });
    },
    ...options,
  });
}

/**
 * Hook for starting metrics aggregation for a merchant
 * @param {Object} options - Additional react-query mutation options
 */
export function useStartMetricsAggregation(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sellerId, range }) =>
      fetcher(
        stringifyUrl({
          url: `/admin/sellers/${sellerId}/aggregate-historical-metrics`,
          query: {
            range,
          },
        }),
        token,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'metric-logs'] });
    },
    ...options,
  });
}

// ============================================================================
// Template Mutations
// ============================================================================

/**
 * Hook for creating a new template
 * @param {Object} options - Additional react-query mutation options
 */
export function useCreateTemplate(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: data =>
      fetcher('/templates', token, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
    },
    ...options,
  });
}

/**
 * Hook for updating an existing template
 * @param {Object} options - Additional react-query mutation options
 */
export function useUpdateTemplate(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, data }) =>
      fetcher(`/templates/${templateId}`, token, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (_, { templateId }) => {
      queryClient.invalidateQueries({ queryKey: adminTemplateKey(templateId) });
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
    },
    ...options,
  });
}

/**
 * Hook for deleting a template
 * @param {Object} options - Additional react-query mutation options
 */
export function useDeleteTemplate(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: templateId =>
      fetcher(`/templates/${templateId}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
    },
    ...options,
  });
}

// ============================================================================
// Template Category Mutations
// ============================================================================

/**
 * Hook for creating a new template category
 * @param {Object} options - Additional react-query mutation options
 */
export function useCreateTemplateCategory(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: data =>
      fetcher('/templates/categories', token, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'template-categories'] });
    },
    ...options,
  });
}

/**
 * Hook for updating an existing template category
 * @param {Object} options - Additional react-query mutation options
 */
export function useUpdateTemplateCategory(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, data }) =>
      fetcher(`/templates/categories/${categoryId}`, token, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (_, { categoryId }) => {
      queryClient.invalidateQueries({ queryKey: adminTemplateCategoryKey(categoryId) });
      queryClient.invalidateQueries({ queryKey: ['admin', 'template-categories'] });
    },
    ...options,
  });
}

/**
 * Hook for deleting a template category
 * @param {Object} options - Additional react-query mutation options
 */
export function useDeleteTemplateCategory(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: categoryId =>
      fetcher(`/templates/categories/${categoryId}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'template-categories'] });
    },
    ...options,
  });
}

/**
 * Hook for taking a template screenshot
 * @param {Object} options - Additional react-query mutation options
 */
export function useTakeTemplateScreenshot(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: templateId =>
      fetcher(`/templates/${templateId}/screenshot`, token, {
        method: 'PUT',
      }),
    onSuccess: (_, templateId) => {
      queryClient.invalidateQueries({ queryKey: adminTemplateKey(templateId) });
    },
    ...options,
  });
}

/**
 * Hook for generating AI template copy content
 * @param {Object} options - Additional react-query mutation options
 */
export function useGenerateTemplateCopy(options = {}) {
  const { token } = useAuth();

  return useMutation({
    mutationFn: data =>
      fetcher('/templates/generate-template-content', token, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    ...options,
  });
}

// ============================================================================
// Statistic Log Mutations
// ============================================================================

/**
 * Hook for deleting statistic logs by status
 * @param {Object} options - Additional react-query mutation options
 */
export function useDeleteStatisticLogs(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ status }) =>
      fetcher(`/admin/statistics/logs?status=${status}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'statistic-logs'] });
    },
    ...options,
  });
}

// ============================================================================
// Metric Log Mutations
// ============================================================================

/**
 * Hook for deleting metric logs by status
 * @param {Object} options - Additional react-query mutation options
 */
export function useDeleteMetricLogs(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ status }) =>
      fetcher(`/admin/metrics/logs?status=${status}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'metric-logs'] });
    },
    ...options,
  });
}

// ============================================================================
// Integration Log Mutations
// ============================================================================

/**
 * Hook for resending an integration log (webhook/integration retry)
 * @param {Object} options - Additional react-query mutation options
 */
export function useResendIntegrationLog(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ integrationId, integrationLogId }) =>
      fetcher(`/integrations/${integrationId}/logs/${integrationLogId}/resend`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'integration-logs'] });
    },
    ...options,
  });
}

// ============================================================================
// Global Admin Actions (All Sellers)
// ============================================================================

/**
 * Hook for starting statistics aggregation for ALL sellers (global admin action)
 * Different from useStartStatisticsAggregate which is for a single seller
 * @param {Object} options - Additional react-query mutation options
 */
export function useStartGlobalStatisticsAggregate(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ range, metric }) =>
      fetcher(
        stringifyUrl({
          url: '/admin/aggregate-historical-statistics',
          query: {
            range,
            ...(metric && { metric }),
          },
        }),
        token,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'statistic-logs'] });
    },
    ...options,
  });
}

/**
 * Hook for starting metrics aggregation for ALL sellers (global admin action)
 * Different from useStartMetricsAggregation which is for a single seller
 * @param {Object} options - Additional react-query mutation options
 */
export function useStartGlobalMetricsAggregation(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ range }) =>
      fetcher(
        stringifyUrl({
          url: '/admin/aggregate-historical-metrics',
          query: {
            range,
          },
        }),
        token,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'metric-logs'] });
    },
    ...options,
  });
}

// ============================================================================
// Brand Fetches Mutations
// ============================================================================

/**
 * Hook for testing brand extraction for multiple domains or emails
 * @param {Object} options - Additional react-query mutation options
 */
export function useTestBrandExtraction(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ inputs }) =>
      fetcher('/admin/brand/test', token, {
        method: 'POST',
        body: JSON.stringify({ inputs }),
      }),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: adminBrandLogKey() });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
}

/**
 * Hook for clearing brand cache for a specific domain
 * @param {Object} options - Additional react-query mutation options
 */
export function useClearBrandCache(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ domain }) =>
      fetcher(`/admin/brand/cache/${encodeURIComponent(domain)}`, token, {
        method: 'DELETE',
      }),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: adminBrandLogKey() });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
}

export function useLiftAiPause(options = {}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId }) =>
      fetcher(`/admin/ai-usage/users/${userId}/lift-pause`, token, { method: 'POST' }),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: adminAiUsageKey });
      options.onSuccess?.(data, variables, context);
    },
  });
}
